// `import type`, not a value import: it is erased at build time, so the
// client bundle does not pull routes/me.ts -> require-admin -> Better Auth in
// behind a two-field interface.
import type { Me } from "@hub/routes/me";
import { AppShell } from "@hub/ui/AppShell";
import { ThemeProvider } from "next-themes";
import { useEffect, useState } from "react";
import { AddInstancePage } from "./AddInstancePage";
import { ChannelsPage } from "./ChannelsPage";
import { InstanceDetailPage } from "./InstanceDetailPage";
import { InstanceTable } from "./InstanceTable";
import { OrganizationsPage } from "./OrganizationsPage";
import { OrgPage } from "./OrgPage";
import { ReleasesPage } from "./ReleasesPage";
import { type HubRoute, routeFor } from "./route";

/**
 * No router dependency — the hub's page set is a handful of routes and none of them
 * nest, so `window.location.pathname` plus plain <a> navigation covers it. The
 * decision itself lives in routeFor() (./route.ts), which is pure and tested;
 * this component only renders what it returns.
 */
function render(route: HubRoute, me: Me | null) {
  switch (route.kind) {
    case "releases":
      return <ReleasesPage />;
    case "instance-new":
      return <AddInstancePage />;
    case "instance-detail":
      return <InstanceDetailPage id={route.id} />;
    case "orgs":
      return <OrganizationsPage />;
    case "org-new":
      return <OrgPage slug={null} nymblTenantId={me?.nymblTenantId ?? null} />;
    case "org-detail":
      return <OrgPage slug={route.slug} nymblTenantId={me?.nymblTenantId ?? null} />;
    case "channels":
      return <ChannelsPage />;
    case "fleet":
      return <InstanceTable />;
    case "loading":
      return <p className="text-sm text-muted-foreground">Loading…</p>;
    case "redirect":
      return null;
  }
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    // Plain fetch, not apiFetch: apiFetch turns a 401 into a redirect to
    // /login, which on a now-public page would bounce every anonymous
    // /releases visitor to a login screen they have no reason to see.
    // A 401 here just means "not signed in" — treat it as "not an
    // operator": the releases page is the safe destination, and the fleet
    // routes would 401 anyway.
    fetch("/api/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((m: Me | null) => setMe(m ?? { email: "", isAdmin: false, nymblTenantId: null }))
      .catch(() => setMe({ email: "", isAdmin: false, nymblTenantId: null }));
  }, []);

  const route = routeFor(window.location.pathname, me === null ? null : me.isAdmin);

  useEffect(() => {
    // `replace`, not `href`: Back should leave the hub, not bounce between /
    // and /releases.
    if (route.kind === "redirect") window.location.replace(route.to);
  }, [route]);

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <AppShell me={me}>{render(route, me)}</AppShell>
    </ThemeProvider>
  );
}
